import { useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import { FlowBox, FlowDecision, FlowArrow, ArrowMarker } from "../components/FlowPrimitives";
import { FileDown, Workflow } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";

const FLOWS = [
  {
    id: "ciclo-ot",
    title: "1 · Ciclo de Vida de una Orden de Trabajo",
    description: "Recorrido completo desde que se crea la OT hasta que se paga al contratista.",
    pitch: "Cada decisión queda trazada. Si una OT se rechaza, queda el motivo. Si pasa todos los controles, fluye automáticamente a facturación.",
  },
  {
    id: "fiscalizacion-ia",
    title: "2 · Fiscalización con Vision AI",
    description: "Cómo la IA pre-clasifica cada foto y desencadena sanciones o aprobaciones automáticas.",
    pitch: "Antes el fiscalizador revisaba foto por foto. Ahora la IA pre-clasifica con un nivel de confianza y solo lo que esté en zona gris pasa por revisión humana.",
  },
  {
    id: "diseno-expansion",
    title: "3 · Diseño y Expansión de Red",
    description: "Desde la solicitud comercial hasta la activación del nodo.",
    pitch: "Toda solicitud de expansión recorre un flujo donde cada área (técnica, comercial, territorio) aprueba o suspende según factibilidad.",
  },
  {
    id: "tres-firmas",
    title: "4 · Pre-Facturación (Flujo de 3 Firmas)",
    description: "Aprobación multinivel antes de liberar pago al contratista.",
    pitch: "Ningún pago se libera sin pasar por las 3 firmas: Coordinador de Control de Calidad, Gerente de Procesos y Contabilidad.",
  },
  {
    id: "alertas-resolucion",
    title: "5 · Alertas y Resolución de OT",
    description: "Cómo el sistema detecta problemas automáticamente y propone soluciones priorizadas.",
    pitch: "Cuando una OT no se cierra, el sistema detecta 8 tipos de problemas y propone la solución con responsable y prioridad.",
  },
  {
    id: "asignacion-inteligente",
    title: "6 · Asignación Inteligente de Cuadrillas",
    description: "Cómo el sistema sugiere la mejor cuadrilla para cada OT pendiente.",
    pitch: "El sistema compara carga actual, experiencia en zona y especialidad técnica para sugerir el técnico óptimo con un score 0-100.",
  },
];

// ---------- SVG content for each flow ----------

function FlowCicloOT() {
  return (
    <svg viewBox="0 0 1100 360" className="w-full h-auto" data-testid="svg-ciclo-ot">
      <ArrowMarker />
      <FlowBox x={20} y={40} type="start" label="Cliente solicita servicio" sublabel="Comercial / Odoo" />
      <FlowArrow from={[200, 65]} to={[240, 65]} />
      <FlowBox x={240} y={40} label="Asignación a técnico" sublabel="Supervisor / IA" testId="step-asignacion" />
      <FlowArrow from={[420, 65]} to={[460, 65]} />
      <FlowBox x={460} y={40} type="ia" label="Sugerir cuadrilla IA" sublabel="Score zona + carga" />
      <FlowArrow from={[640, 65]} to={[680, 65]} />
      <FlowBox x={680} y={40} label="Técnico ejecuta" sublabel="App móvil + GPS" />
      <FlowArrow from={[860, 65]} to={[900, 65]} />
      <FlowBox x={900} y={40} label="Carga evidencias" sublabel="Fotos + Speedtest" />

      <FlowArrow from={[990, 90]} to={[990, 145]} />
      <FlowDecision x={920} y={145} label="¿Veredicto IA?" testId="dec-veredicto" />
      <FlowArrow from={[990, 215]} to={[990, 250]} />

      <FlowBox x={900} y={250} type="success" label="Validada" />
      <FlowArrow from={[920, 215]} to={[800, 215]} label="Obs." color="#EF4444" />
      <FlowBox x={620} y={195} type="danger" label="Genera No Conformidad" sublabel="+ sanción al contratista" />
      <FlowArrow from={[620, 220]} to={[460, 220]} />
      <FlowBox x={280} y={195} type="decision" label="¿Corrige?" />
      <FlowArrow from={[280, 220]} to={[200, 220]} label="Sí" color="#10B981" />
      <FlowBox x={20} y={195} type="success" label="Reabrir y validar" />
      <FlowArrow from={[345, 245]} to={[345, 290]} label="No" color="#EF4444" />
      <FlowBox x={255} y={290} type="danger" label="Rechazada · Descuento" />

      <FlowArrow from={[1000, 300]} to={[1060, 300]} curve={false} />
      <FlowBox x={900} y={290} type="end" label="Facturada" />
    </svg>
  );
}

function FlowFiscalizacionIA() {
  return (
    <svg viewBox="0 0 1100 360" className="w-full h-auto" data-testid="svg-fiscalizacion-ia">
      <ArrowMarker />
      <FlowBox x={20} y={40} type="start" label="Foto subida por técnico" sublabel="App F/Cell + marca agua" />
      <FlowArrow from={[200, 65]} to={[260, 65]} />
      <FlowBox x={260} y={40} type="ia" label="Vision AI analiza" sublabel="Modelo clasificador" />
      <FlowArrow from={[440, 65]} to={[500, 65]} />
      <FlowDecision x={490} y={30} label="Confianza ≥ 90%" testId="dec-confianza" />

      <FlowArrow from={[630, 65]} to={[700, 65]} label="Sí" color="#10B981" />
      <FlowBox x={700} y={40} type="success" label="Auto-validada" sublabel="Sin intervención humana" />

      <FlowArrow from={[560, 100]} to={[560, 160]} label="<90%" color="#F59E0B" />
      <FlowBox x={470} y={160} label="Revisión manual" sublabel="Fiscalizador" />

      <FlowArrow from={[560, 210]} to={[560, 260]} />
      <FlowDecision x={490} y={250} label="¿Aprueba?" testId="dec-aprueba" />

      <FlowArrow from={[490, 285]} to={[400, 285]} label="No" color="#EF4444" />
      <FlowBox x={210} y={260} type="danger" label="No Conformidad" sublabel="+ Sanción USD" />

      <FlowArrow from={[210, 285]} to={[150, 285]} />
      <FlowBox x={20} y={260} label="Notificar contratista" />

      <FlowArrow from={[630, 285]} to={[720, 285]} label="Sí" color="#10B981" />
      <FlowBox x={720} y={260} type="success" label="Validada manual" />

      <FlowArrow from={[890, 65]} to={[990, 65]} curve={false} />
      <FlowBox x={970} y={40} type="end" label="OT lista" />
      <FlowArrow from={[810, 285]} to={[990, 95]} curve={true} />
    </svg>
  );
}

function FlowDisenoExpansion() {
  return (
    <svg viewBox="0 0 1100 360" className="w-full h-auto" data-testid="svg-diseno">
      <ArrowMarker />
      <FlowBox x={20} y={40} type="start" label="Cliente pide expansión" sublabel="Sector sin cobertura" />
      <FlowArrow from={[200, 65]} to={[260, 65]} />
      <FlowBox x={260} y={40} label="Análisis técnico" sublabel="Coordinador Datos" />
      <FlowArrow from={[440, 65]} to={[500, 65]} />
      <FlowDecision x={490} y={30} label="¿Factibilidad?" testId="dec-factibilidad" />

      <FlowArrow from={[560, 100]} to={[560, 160]} label="Parcial" color="#F59E0B" />
      <FlowBox x={470} y={160} label="Permisos CNEL" sublabel="Municipio" />
      <FlowArrow from={[490, 185]} to={[400, 185]} />
      <FlowBox x={260} y={160} label="Re-evaluación" />

      <FlowArrow from={[630, 65]} to={[700, 65]} label="Sí" color="#10B981" />
      <FlowBox x={700} y={40} label="Líder Territorio" sublabel="Aprobación comercial" />
      <FlowArrow from={[880, 65]} to={[920, 65]} />
      <FlowDecision x={910} y={30} label="¿Aprueba?" />

      <FlowArrow from={[980, 100]} to={[980, 160]} label="No" color="#EF4444" />
      <FlowBox x={890} y={160} type="danger" label="Suspendida" sublabel="Aux. Control" />

      <FlowArrow from={[980, 65]} to={[1080, 65]} curve={false} />
      <FlowBox x={920} y={195} label="Diseño tendido" sublabel="DOT generada" />
      <FlowArrow from={[1000, 220]} to={[1000, 260]} />
      <FlowBox x={910} y={260} type="end" label="Nodo activado" />

      <FlowArrow from={[490, 185]} to={[180, 185]} label="No" color="#EF4444" curve={true} />
      <FlowBox x={20} y={160} type="danger" label="Rechazada" sublabel="Sin factibilidad" />
    </svg>
  );
}

function FlowTresFirmas() {
  return (
    <svg viewBox="0 0 1100 340" className="w-full h-auto" data-testid="svg-tres-firmas">
      <ArrowMarker />
      <FlowBox x={20} y={140} type="start" label="OT facturable" sublabel="status: validada" />
      <FlowArrow from={[200, 165]} to={[260, 165]} />
      <FlowBox x={260} y={140} label="Pre-factura generada" sublabel="Odoo + Alfa-Field" />

      <FlowArrow from={[440, 165]} to={[500, 165]} />
      <FlowBox x={500} y={140} type="decision" label="① Coord. Control Calidad" sublabel="Valida informe" />
      <FlowArrow from={[590, 190]} to={[590, 250]} label="✓" color="#10B981" />
      <FlowBox x={500} y={250} type="decision" label="② Gerente Procesos" sublabel="Aprueba monto" />

      <FlowArrow from={[680, 275]} to={[760, 275]} />
      <FlowBox x={760} y={250} type="decision" label="③ Contabilidad" sublabel="Confirma recepción" />

      <FlowArrow from={[850, 250]} to={[850, 190]} label="✓" color="#10B981" />
      <FlowBox x={760} y={140} type="success" label="Aprobada" sublabel="Pago liberado" />

      <FlowArrow from={[940, 165]} to={[1000, 165]} />
      <FlowBox x={920} y={140} type="end" label="Contabilidad" sublabel="Programa pago" />

      <FlowArrow from={[500, 165]} to={[440, 80]} label="✗" color="#EF4444" curve={true} />
      <FlowArrow from={[500, 275]} to={[440, 80]} label="✗" color="#EF4444" curve={true} />
      <FlowArrow from={[760, 275]} to={[440, 80]} label="✗" color="#EF4444" curve={true} />
      <FlowBox x={260} y={50} type="danger" label="Rechazada" sublabel="WhatsApp al contratista" />

      <FlowArrow from={[260, 70]} to={[200, 70]} />
      <FlowBox x={20} y={50} label="Contratista corrige" />
      <FlowArrow from={[110, 100]} to={[110, 140]} />
    </svg>
  );
}

function FlowAlertas() {
  return (
    <svg viewBox="0 0 1100 360" className="w-full h-auto" data-testid="svg-alertas">
      <ArrowMarker />
      <FlowBox x={20} y={40} type="start" label="OT no cerrada" sublabel="status ≠ facturada" />
      <FlowArrow from={[200, 65]} to={[260, 65]} />
      <FlowBox x={260} y={40} type="ia" label="Motor de alertas IA" sublabel="Analiza 8 dimensiones" />
      <FlowArrow from={[440, 65]} to={[500, 65]} />

      <FlowBox x={500} y={20} type="danger" w={160} h={36} label="🔴 EPP no validado" testId="alert-epp" />
      <FlowBox x={500} y={62} type="danger" w={160} h={36} label="🔴 Discrepancia GPS >5%" />
      <FlowBox x={500} y={104} w={160} h={36} label="🟡 Marca agua incorrecta" />
      <FlowBox x={680} y={20} w={160} h={36} label="🟡 Tracking detenido" />
      <FlowBox x={680} y={62} w={160} h={36} label="🟡 Materiales sin reg." />
      <FlowBox x={680} y={104} type="danger" w={160} h={36} label="🔴 Potencia fuera rango" />

      <FlowArrow from={[760, 145]} to={[760, 180]} />
      <FlowDecision x={690} y={180} label="¿Score < 50?" testId="dec-score" />

      <FlowArrow from={[830, 215]} to={[930, 215]} label="Sí" color="#EF4444" />
      <FlowBox x={920} y={195} type="danger" label="Bloqueo cierre" sublabel="Alta prioridad" />

      <FlowArrow from={[690, 215]} to={[600, 215]} label="No" color="#10B981" />
      <FlowBox x={420} y={195} label="Notificar responsable" sublabel="Solución sugerida" />

      <FlowArrow from={[510, 235]} to={[510, 280]} />
      <FlowBox x={420} y={280} label="Técnico corrige" sublabel="Re-fiscalización" />

      <FlowArrow from={[420, 305]} to={[200, 305]} />
      <FlowBox x={20} y={280} type="success" label="Cierre permitido" sublabel="Score = 100" />
    </svg>
  );
}

function FlowAsignacion() {
  return (
    <svg viewBox="0 0 1100 320" className="w-full h-auto" data-testid="svg-asignacion">
      <ArrowMarker />
      <FlowBox x={20} y={130} type="start" label="OT pendiente asignar" />
      <FlowArrow from={[200, 155]} to={[260, 155]} />
      <FlowBox x={260} y={130} type="ia" label="IA Asignación" sublabel="Score por técnico" />

      <FlowArrow from={[350, 130]} to={[350, 60]} />
      <FlowBox x={290} y={20} label="Carga actual" sublabel="Órdenes abiertas" />
      <FlowArrow from={[440, 155]} to={[510, 155]} />
      <FlowBox x={510} y={130} label="Experiencia zona" sublabel="OT en mismo sector" />
      <FlowArrow from={[600, 130]} to={[600, 60]} />
      <FlowBox x={540} y={20} label="Especialidad" sublabel="FTTH / Radio Enlace" />

      <FlowArrow from={[690, 155]} to={[760, 155]} />
      <FlowBox x={760} y={130} type="success" label="Ranking 1-5" sublabel="Mejor opción → 100" />

      <FlowArrow from={[940, 155]} to={[990, 155]} />
      <FlowBox x={970} y={130} label="Supervisor aprueba" />

      <FlowArrow from={[1060, 180]} to={[1060, 240]} />
      <FlowArrow from={[1060, 240]} to={[180, 240]} />
      <FlowBox x={20} y={240} type="end" label="OT asignada" sublabel="+ notificación" />
    </svg>
  );
}

const FLOW_SVGS = {
  "ciclo-ot": <FlowCicloOT />,
  "fiscalizacion-ia": <FlowFiscalizacionIA />,
  "diseno-expansion": <FlowDisenoExpansion />,
  "tres-firmas": <FlowTresFirmas />,
  "alertas-resolucion": <FlowAlertas />,
  "asignacion-inteligente": <FlowAsignacion />,
};

export default function FlujosDecision() {
  const containerRef = useRef(null);
  const location = useLocation();

  // Scroll al flujo indicado por hash (#flow-id)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const timer = setTimeout(() => {
      const el = document.getElementById(`flow-card-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-[#E0E621]");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#E0E621]"), 2200);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [location.hash]);

  const exportPDF = async () => {
    try {
      toast.info("Generando PDF de flujos…");
      const html2canvas = (await import("html2canvas")).default;
      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      // Cover page
      doc.setFillColor(10, 26, 36);
      doc.rect(0, 0, pageW, pageH, "F");
      doc.setFillColor(200, 230, 0);
      doc.rect(0, pageH * 0.55, pageW, 1.2, "F");
      doc.setTextColor(200, 230, 0);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.text("TELEALFACOM S.A.", 20, 25);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(36);
      doc.text("Alfa-Field Connect", 20, 90);
      doc.setTextColor(200, 230, 0);
      doc.setFontSize(20);
      doc.text("Flujos de Decisión", 20, 105);
      doc.setTextColor(180, 180, 180);
      doc.setFontSize(10);
      doc.text("Diagramas de proceso para presentación gerencial", 20, 115);
      doc.text(`Generado el ${new Date().toLocaleDateString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "long", year: "numeric" })}`, 20, pageH - 18);

      // Each flow on its own page
      for (const flow of FLOWS) {
        const el = document.getElementById(`flow-card-${flow.id}`);
        if (!el) continue;
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#FFFFFF" });
        const img = canvas.toDataURL("image/png");
        doc.addPage();
        // Header band
        doc.setFillColor(10, 26, 36);
        doc.rect(0, 0, pageW, 12, "F");
        doc.setFillColor(200, 230, 0);
        doc.rect(0, 12, pageW, 0.6, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(flow.title, 14, 8.5);

        // Image — fit width
        const imgW = pageW - 16;
        const imgH = (canvas.height / canvas.width) * imgW;
        doc.addImage(img, "PNG", 8, 18, imgW, Math.min(imgH, pageH - 30));

        // Footer
        doc.setDrawColor(229, 231, 235);
        doc.line(14, pageH - 8, pageW - 14, pageH - 8);
        doc.setFontSize(7);
        doc.setTextColor(107, 114, 128);
        doc.setFont("helvetica", "normal");
        doc.text("Alfa-Field Connect · Confidencial", 14, pageH - 4);
      }

      doc.save("AlfaFieldConnect_Flujos_Decision.pdf");
      toast.success("PDF descargado");
    } catch (e) {
      console.error(e);
      toast.error("Error al generar PDF");
    }
  };

  return (
    <Layout
      title="Flujos de Decisión"
      subtitle="Diagramas para presentación gerencial"
      actions={
        <button
          onClick={exportPDF}
          className="btn-primary flex items-center gap-2 text-sm"
          data-testid="export-flows-pdf"
        >
          <FileDown size={15} /> Descargar todos en PDF
        </button>
      }
    >

      <div className="kpi-card mb-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[#F0FFCE] text-[#0A3747] flex items-center justify-center flex-shrink-0">
            <Workflow size={18} />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider">
              Manual de procesos visuales
            </div>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "Chivo" }}>
              6 flujos de decisión documentados
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Cada flujo muestra cómo el sistema decide automáticamente y cuándo interviene el ser humano.
              Úsalos durante la presentación a gerencia para ilustrar las eficiencias logradas.
            </p>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="space-y-6">
        {FLOWS.map((f, i) => (
          <div key={f.id} id={`flow-card-${f.id}`} className="kpi-card" data-testid={`flow-card-${f.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider">
                  Flujo {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>
                  {f.title.replace(/^\d+ · /, "")}
                </h3>
                <p className="text-sm text-gray-600 mt-1 max-w-3xl">{f.description}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 overflow-x-auto">
              {FLOW_SVGS[f.id]}
            </div>

            <div className="mt-4 border-l-2 border-[#E0E621] bg-[#F0FFCE]/30 px-4 py-2.5 rounded-r">
              <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider mb-1">
                Cómo presentarlo
              </div>
              <p className="text-sm italic text-gray-800">{f.pitch}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Leyenda */}
      <div className="kpi-card mt-6">
        <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider mb-2">Leyenda</div>
        <h3 className="font-bold mb-3" style={{ fontFamily: "Chivo" }}>Convenciones visuales</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
          <LegendItem color="#0A3747" label="Inicio / Fin" />
          <LegendItem color="#FFFFFF" border="#0A3747" label="Proceso" />
          <LegendItem color="#FEF3C7" border="#F59E0B" label="Decisión" diamond />
          <LegendItem color="#F0FFCE" border="#E0E621" label="IA / Automático" />
          <LegendItem color="#DCFCE7" border="#10B981" label="Éxito" />
          <LegendItem color="#FEE2E2" border="#EF4444" label="Error / Rechazo" />
        </div>
      </div>
    </Layout>
  );
}

function LegendItem({ color, border, label, diamond }) {
  return (
    <div className="flex items-center gap-2">
      {diamond ? (
        <div
          className="w-5 h-5 transform rotate-45 border-2"
          style={{ background: color, borderColor: border || color }}
        />
      ) : (
        <div
          className="w-5 h-5 rounded border-2"
          style={{ background: color, borderColor: border || color }}
        />
      )}
      <span>{label}</span>
    </div>
  );
}
